import { Injectable } from '@angular/core';
import { User } from '../models/user';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { compare } from 'fast-json-patch';
import { Profile, Profiles } from '../models/profile';
import { Domain } from '@models/domain';

@Injectable({
  providedIn: 'root'
})
export class ProfileService {
  private readonly baseUrl;

  constructor(private http: HttpClient) {
    this.baseUrl = environment.serverBaseUrl;
  }

  getProfiles(pageNum?, pageSize?, sortColumn?, sortDirection?): Observable<Profiles> {
    let params = new HttpParams();
    if (pageNum) {
      params = params.append('page', pageNum);
    }
    if (pageSize) {
      params = params.append('size', pageSize);
    }
    if (sortColumn) {
      params = params.append('sort-column', sortColumn);
    }
    if (sortDirection) {
      params = params.append('sort-direction', sortDirection);
    }
    return this.http
      .get<Response>(this.baseUrl + 'user-profile/list', { params })
      .pipe(
        map(data => {
          return Profiles.deserialize({
            data: (data ? ((data as any) as Array<string>) : []).map(data2 => Profile.deserialize(data2)),
            total: (data as any).length
          });
        })
      );
  }

  addProfile(profile: Profile) {
    return this.http.post(this.baseUrl + 'user-profile', profile, { responseType: 'text' as 'json' });
  }

  removeProfile(profile: Profile) {
    return this.http.delete(this.baseUrl + 'user-profile/' + profile.idUserProfile, { responseType: 'text' as 'json' });
  }
  //
  // editField(contactField: ContactField) {
  //   return this.http.put(this.baseUrl + 'contacts/fields/personalizable/' + contactField.idField, contactField.label, {
  //     responseType: 'text' as 'json'
  //   });
  // }

  public updateProfile(profile: Profile, formData: Profile): Observable<User> {
    const profilePatch = compare(profile, formData).filter(
      x => x.path !== '/idAuth0' && x.path !== '/idUser' && x.path !== '/registrationEmail' && x.path !== '/userName'
    );
    const patchHeaders = new HttpHeaders().set('Content-type', 'application/json-patch+json');
    return this.http
      .patch<Response>(this.baseUrl + 'user-profile/' + profile.idUserProfile, profilePatch, { headers: patchHeaders })
      .pipe(map(data => Profile.deserialize(data)));
  }

  getLoggedProfile(): Observable<Profile> {
    return this.http.get<Response>(this.baseUrl + 'user-profile').pipe(map(data => Profile.deserialize(data)));
  }

  public updateLoggedProfile(profile: Profile, formData: Profile): Observable<User> {
    const profilePatch = compare(profile, formData);
    const patchHeaders = new HttpHeaders().set('Content-type', 'application/json-patch+json');
    return this.http
      .patch<Response>(this.baseUrl + 'user-profile', profilePatch, { headers: patchHeaders })
      .pipe(map(data => Profile.deserialize(data)));
  }

  getRoles(): Observable<Array<object>> {
    return this.http.get<Response>(this.baseUrl + 'user-role').pipe(
      map(data => {
        return (data as unknown) as Array<object>;
      })
    );
  }

  checkActivationToken(token) {
    return this.http.post(
      this.baseUrl + 'user-profile/check-activation-token',
      {
        check: token
      },
      { responseType: 'text' as 'json' }
    );
  }

  activate(password, token) {
    return this.http.post(
      this.baseUrl + 'user-profile/activate',
      {
        password,
        check: token
      },
      { responseType: 'text' as 'json' }
    );
  }

  resetSend(username: string): Observable<any> {
    const queryParams = new HttpParams().set('username', username);
    const headers = new HttpHeaders().set('Content-type', 'application/json');
    return this.http.get<Response>(this.baseUrl + 'password/send-reset-email', { params: queryParams, headers });
  }
  resetConfirm(password, token) {
    return this.http.post(
      this.baseUrl + 'password/reset-by-token',
      {
        password,
        token
      },
      { responseType: 'text' as 'json' }
    );
  }
  sendEmailChange(email) {
    return this.http.post(
      this.baseUrl + 'user-profile/email',
      {
        newEmail: email
      },
      { responseType: 'text' as 'json' }
    );
  }
  confirmEmailChange(newEmail, token) {
    return this.http.post(
      this.baseUrl + 'user-profile/email/reset-by-token',
      {
        newEmail,
        token
      },
      { responseType: 'text' as 'json' }
    );
  }

  addEmailRegistration(email) {
    return this.http.post(
      this.baseUrl + 'user-profile/email/registration',
      {
        newEmail: email
      },
      { responseType: 'text' as 'json' }
    );
  }

  askAuth0ResetPassword(domain: Domain, email: string) {
    const auth0Domain = domain.customDomain;
    const clientId = domain.clientId;
    const connection = 'Username-Password-Authentication';
    if (auth0Domain && clientId) {
      return this.http.post(
        auth0Domain + 'dbconnections/change_password',
        {
          clientId,
          email,
          connection
        },
        { responseType: 'text' as 'json' }
      );
    }
    return of(null);
  }
}
